import type { PollWithOptionsResults } from "../../../../modules/poll/server/supabase-poll-service";
import { filterStatType } from "../../../../modules/stats/server/analytics-service";
import { notEmpty } from "../../../../utils/not-empty";
import type { TransformedGroupedMomentAnalytics } from "../../analytics/hooks/utils";
import {
  type PollRecipient,
  type PollRecipientResponse,
  type RenderPollResults,
} from "./moment-polls-context";

// When we are ready, allow multiple answers per person.
const ALLOW_MULTIPLE_ANSWERS = false;

// This is a complex function which loops through the returned options
// and their responses, and removes all except the most recent response/answer
// for each person. The comparison operates across options for the same person.
// It returns the data in the same structure as the original data.
// It defines a person using the person_id only.
const filterPreviousAnswersInOptions = (
  options: PollWithOptionsResults["poll"]["options"],
): PollWithOptionsResults["poll"]["options"] => {
  if (ALLOW_MULTIPLE_ANSWERS) {
    return options;
  }
  // Assemble a map of the most recent response for each person_id
  // and the timestamp of that response.
  const mostRecentResponseTimestampByPersonId = new Map<string, number>();
  options.forEach(({ results }) => {
    results.forEach(({ person_id, timestamp: timestampString }) => {
      const timestamp = new Date(timestampString).getTime();
      const lastTimestamp = mostRecentResponseTimestampByPersonId.get(person_id);
      if (!lastTimestamp || timestamp > lastTimestamp) {
        mostRecentResponseTimestampByPersonId.set(person_id, timestamp);
      }
    });
  });

  // Now filter the options to only include the most recent response for each person_id
  return options.map((option) => ({
    ...option,
    results: option.results.filter(({ person_id, timestamp: timestampString }) => {
      const timestamp = new Date(timestampString).getTime();
      const lastTimestamp = mostRecentResponseTimestampByPersonId.get(person_id);
      return lastTimestamp === timestamp;
    }),
  }));
};

export const extractDelivereds = ({
  rows,
}: {
  rows: TransformedGroupedMomentAnalytics[];
}): PollRecipient[] => {
  const allStats = rows.flatMap(({ rawData }) => rawData.stats).flat();
  const deliveryStats = filterStatType("delivery", allStats);
  const deliveries = deliveryStats
    .map(({ data }) => data.recipient_data)
    .filter(notEmpty)
    .filter(({ person_id }) => !!person_id) as PollRecipient[];

  return deliveries;
};

const assembleRecipientResponses = (
  option: PollWithOptionsResults["poll"]["options"][number],
  delivereds: PollRecipient[],
): PollRecipientResponse[] => {
  return option.results.map((result) => {
    const delivery = delivereds.find(
      (del) => del.person_id === result.person_id || del.destination === result.destination,
    );

    return {
      ...result,
      first_name: result.first_name || delivery?.first_name || null,
      last_name: result.last_name || delivery?.last_name || null,
      email: result.email || delivery?.email || null,
      display: delivery?.display || null,
    };
  });
};

const calculatePollResults = (
  { poll }: PollWithOptionsResults,
  { total_sends }: { total_sends?: number },
  delivereds: PollRecipient[],
): RenderPollResults => {
  const filteredOptions = filterPreviousAnswersInOptions(poll.options);

  const allRespondees = filteredOptions.flatMap((option) => option.results);
  const totalResponses = allRespondees.length;

  const noResponses = delivereds.filter(
    (delivery) =>
      !allRespondees.some(
        (response) =>
          response.person_id === delivery.person_id ||
          response.destination === delivery.destination,
      ),
  );

  const results = filteredOptions
    .map((option) => {
      const responses = assembleRecipientResponses(option, delivereds);

      if (option.deleted_at && responses.length === 0) return null;

      const count = option.results.length;
      const percentage = totalResponses ? Math.round((count / totalResponses) * 100) : 0;

      return {
        text: option.text,
        count,
        percentage,
        responses,
      };
    })
    .filter(notEmpty);

  return {
    poll_id: poll.id,
    results,
    hasResponsed: totalResponses > 0,
    totalResponses,
    question: poll.question,
    noResponses,
    sentTimes: total_sends || 0,
  };
};

export const calculatePollsResults = (
  momentPollResults: PollWithOptionsResults[],
  { total_sends }: { total_sends?: number },
  delivereds: PollRecipient[],
): RenderPollResults[] =>
  momentPollResults.map((poll) => calculatePollResults(poll, { total_sends }, delivereds));
